import { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Form, Button, Alert, } from "react-bootstrap";
// import { BsChevronRight, BsChevronDown } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import "../styles/InvoiceModal.css";
import { CREATE_INVOICE_REQUEST } from "../store/Actions/Actions";
import Axios from "../utils/Axios";
import { INVOICE_LIST_BY_PROJECT } from "../config/API_Collection";

const NewInvoiceModal = ({ show, onHide, clientId, projectId, budgets = [], onSuccess, onToast }) => {
  const dispatch = useDispatch();
  const { createLoading, error } = useSelector((state) => state.invoices);
  
  // Project details shape: { isLoading, project }
  const project = useSelector((state) => state?.projectDetails?.project);
  
  const [formData, setFormData] = useState({
    client_id: clientId || project?.project_data?.client_id || "",
    project_id: projectId || project?.project_data?.pid || project?.project_data?.id || "",
    invoice_title: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    })(),
    billing_contact: "",
    reference: "",
    description: "",
    gst: 0.15,
    invoice_items: [{ budget_id: "", amount: "" }],
  });

  // Update form when props change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      client_id: clientId || project?.project_data?.client_id || "",
      project_id: projectId || project?.project_data?.pid || project?.project_data?.id || "",
    }));
  }, [clientId, projectId, project]);

  // Auto-fill client name and budget items when modal opens or data changes
  useEffect(() => {
    const nameFromProject = project?.project_data?.client || project?.project_data?.client_name || "";
    if (nameFromProject) {
      setFormData((prev) => ({ ...prev, client_name: nameFromProject }));
    }

    // Build invoice item rows from budget tasks so each task becomes an editable row
    if (Array.isArray(budgets) && budgets.length > 0) {
      const buildItemsFromBudgets = (budgetsList) => {
        const rows = [];
        budgetsList.forEach((b) => {
          const bid = b.bid ?? b.id ?? b.budget_id;
          const tasks = Array.isArray(b.tasks) ? b.tasks : [];
          if (tasks.length === 0) {
            // fallback: keep a budget-level row
            rows.push({
              budget_id: bid,
              budget_name: b.budget_name || b.name || "",
              task_id: null,
              task_name: b.budget_name || "Budget total",
              user_fullname: "",
              comment: "",
              worked_hours: 0,
              rate: 0,
              amount: parseFloat(b.amount || b.cost_spent || 0) || 0,
              _raw: b,
            });
          } else {
            tasks.forEach((t) => {
              // default task row amounts to empty so they don't contribute to the invoice subtotal
              // until the user explicitly enters a value for the budget
              rows.push({
                budget_id: bid,
                budget_name: b.budget_name || b.name || "",
                task_id: t.task_id ?? t.id ?? null,
                task_name: t.task_name ?? t.name ?? t.description ?? "",
                user_fullname: (t.assignments && t.assignments[0]) ? `${t.assignments[0].first_name} ${t.assignments[0].last_name}` : (t.user_fullname || t.user_name || t.assigned_to || ""),
                comment: t.comment || t.notes || t.description || "",
                worked_hours: parseFloat(t.hours_spent ?? t.worked_hours ?? 0) || 0,
                rate: parseFloat(t.rate ?? t.hourly_rate ?? 0) || 0,
                amount: "",
                _raw: t,
              });
            });
          }
        });
        return rows;
      };

      const updatedItems = buildItemsFromBudgets(budgets);
      setFormData((prev) => ({ ...prev, invoice_items: updatedItems }));
    }
  }, [project, budgets, show]);

    // When modal opens, auto-fill billing_contact with contact_name and client_email from project details
    useEffect(() => {
      if (!show) return;
      
      // Get contact name and email from project data
      const contactName = project?.project_data?.contact_name || '';
      const clientEmail = project?.project_data?.client_email || '';
      
      // Format billing contact as "Name (email)" or just email if no name
      let billingContact = '';
      if (contactName && clientEmail) {
        billingContact = `${contactName} (${clientEmail})`;
      } else if (clientEmail) {
        billingContact = clientEmail;
      } else if (contactName) {
        billingContact = contactName;
      }
      
      // Only update if we have data and the field is empty
      if (billingContact && (!formData.billing_contact || formData.billing_contact === "")) {
        setFormData(prev => ({ ...prev, billing_contact: billingContact }));
      }
    }, [show, project, formData.billing_contact]);

  // Debug: when modal opens, log budget vs invoice item amounts to help find unexpected non-zero values
  useEffect(() => {
    if (!show) return;
    try {
      const items = Array.isArray(formData.invoice_items) ? formData.invoice_items : [];
      const perBudget = (Array.isArray(budgets) ? budgets : []).map((b) => {
        const bid = b.bid ?? b.id ?? b.budget_id;
        const fallback = parseFloat(b.budget ?? b.amount ?? b.cost_spent ?? 0) || 0;
        const matching = items.find(it => String(it?.budget_id) === String(bid));
        return { bid, fallback, matchingAmount: matching?.amount };
      });
      console.debug('[Invoice Debug] perBudget', perBudget);
      const nonZeroMatch = perBudget.filter(p => p.matchingAmount !== undefined && p.matchingAmount !== null && Number(p.matchingAmount) && Number(p.matchingAmount) !== 0);
      if (nonZeroMatch.length) console.warn('[Invoice Debug] Non-zero matching amounts found (user-entered or prefilled):', nonZeroMatch);
      const nonZeroFallback = perBudget.filter(p => (p.matchingAmount === undefined || p.matchingAmount === null || p.matchingAmount === "") && p.fallback && Number(p.fallback) !== 0);
      if (nonZeroFallback.length) console.warn('[Invoice Debug] Non-zero fallback budget amounts (from budget data):', nonZeroFallback);
      console.debug('[Invoice Debug] full invoice_items', items);
    } catch (e) {
      console.error('[Invoice Debug] error building debug info', e);
    }
  }, [show, budgets, formData.invoice_items]);

  // invoice_items on open: clear any non-positive or unexpected amounts so defaults don't affect totals
  useEffect(() => {
    if (!show) return;
    setFormData((prev) => {
      const items = Array.isArray(prev.invoice_items) ? prev.invoice_items.map(it => {
        const cleaned = it && it.amount !== undefined && it.amount !== null && it.amount !== "" ? Number(it.amount) : "";
        return { ...it, amount: (isNaN(cleaned) || Number(cleaned) === 0) ? "" : Number(cleaned) };
      }) : [];
      return { ...prev, invoice_items: items };
    });

    // log if there are still signs of prefilled amounts in budgets
    try {
      const items = Array.isArray(formData.invoice_items) ? formData.invoice_items : [];
      const perBudget = (Array.isArray(budgets) ? budgets : []).map((b) => {
        const bid = b.bid ?? b.id ?? b.budget_id;
        const fallback = parseFloat(b.budget ?? b.amount ?? b.cost_spent ?? 0) || 0;
        const matching = items.find(it => String(it?.budget_id) === String(bid));
        return { bid, fallback, matchingAmount: matching?.amount };
      });
      const stillNonZero = perBudget.some(p => Number(p.matchingAmount) && Number(p.matchingAmount) !== 0);
      if (stillNonZero) console.warn('[Invoice Debug] After sanitise: non-zero matching amounts present', perBudget.filter(p => Number(p.matchingAmount) && Number(p.matchingAmount) !== 0));
    } catch (e) {
      // ignore
    }
  }, [show, budgets]);

  const wasSubmittingRef = useRef(false);
  const [validationFields, setValidationFields] = useState({});
  const [dueDateTouched, setDueDateTouched] = useState(false);

  useEffect(() => {
    if (dueDateTouched) return;
    const invoiceDate = formData.invoice_date || new Date().toISOString().split('T')[0];
    try {
      const d = new Date(invoiceDate);
      d.setDate(d.getDate() + 7);
      const newDue = d.toISOString().split('T')[0];
      if (formData.due_date !== newDue) {
        setFormData((p) => ({ ...p, due_date: newDue }));
      }
    } catch (e) {
      // ignore invalid dates
    }
  }, [formData.invoice_date, dueDateTouched]);

  // Handle successful / failed creation after a submit
  useEffect(() => {
    if (!show) return;
    if (wasSubmittingRef.current && !createLoading) {
      if (!error) {
        onSuccess && onSuccess();
        resetForm();
      } else {
        onToast && onToast(error, 'error');
      }
      wasSubmittingRef.current = false;
    }
  }, [createLoading, error, show, onSuccess, onToast]);

  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error("Invoice creation error:", error);
    }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // clear validation errors when user edits fields
    if (validationFields[name]) {
      setValidationFields(prev => { const copy = { ...prev }; delete copy[name]; return copy; });
    }
    if (name === "due_date") {
      setDueDateTouched(true);
    }
  };

  // const handleItemChange = (index, field, value) => {
  //   const updatedItems = Array.isArray(formData.invoice_items) ? [...formData.invoice_items] : [];
  //   if (index < 0 || index >= updatedItems.length) return;
  //   if (field === 'amount') {
  //     // sanitize numeric input
  //     const cleaned = String(value).replace(/[^0-9.\-]/g, '');
  //     updatedItems[index] = { ...updatedItems[index], [field]: cleaned === '' ? '' : parseFloat(cleaned) };
  //   } else {
  //     updatedItems[index] = { ...updatedItems[index], [field]: value };
  //   }
  //   setFormData(prev => ({ ...prev, invoice_items: updatedItems }));
  //   // clear validation errors when user edits item amounts
  //   // also try to clear budget-specific validation if we can find budget id at this index
  //   const bid = updatedItems[index]?.budget_id ?? updatedItems[index]?._raw?.bid;
  //   if (bid && validationFields[`budget_${bid}`]) {
  //     setValidationFields(prev => { const copy = { ...prev }; delete copy[`budget_${bid}`]; return copy; });
  //   }
  // };

  // Update or append an invoice item amount by budget id (robust against index mismatches)
  const updateInvoiceItemAmountForBudget = (budgetId, rawValue) => {
    const cleaned = String(rawValue).replace(/[^0-9.\-]/g, '');
    const amount = cleaned === '' ? '' : parseFloat(cleaned);
    setFormData(prev => {
      const items = Array.isArray(prev.invoice_items) ? [...prev.invoice_items] : [];
      const idx = items.findIndex(it => String(it?.budget_id) === String(budgetId));
      if (idx !== -1) {
        items[idx] = { ...items[idx], amount };
      } else {
        items.push({ budget_id: budgetId, amount });
      }
      return { ...prev, invoice_items: items };
    });
    // clear validation for this budget if present
    if (validationFields[`budget_${budgetId}`]) {
      setValidationFields(prev => { const copy = { ...prev }; delete copy[`budget_${budgetId}`]; return copy; });
    }
  };

  const buildSubmitData = () => {
    const fieldErrors = {};
    // Validate required fields (mark per-field errors)
    if (!formData.invoice_title) fieldErrors.invoice_title = "Invoice Title is required.";
    if (!formData.due_date) fieldErrors.due_date = "Due Date is required.";
    if (!formData.billing_contact) fieldErrors.billing_contact = "Billing Contact is required.";
    if (!formData.description || String(formData.description).trim().length === 0) fieldErrors.description = "Description is required.";
    // `reference` is optional — do not mark it as required

    // Validate invoice items: ensure at least one valid and set per-budget errors for missing amounts
    const validItems = (formData.invoice_items || []).filter(item =>
      item && item.budget_id && item.amount !== "" && item.amount !== null &&
      !isNaN(parseInt(item.budget_id)) && !isNaN(parseFloat(item.amount))
    );
    if (validItems.length === 0) {
      // mark each budget amount field as required
      (budgets || []).forEach((b) => {
        const bid = b.bid ?? b.id ?? b.budget_id;
        //fieldErrors[`budget_${bid}`] = "Enter an amount for this budget1.";
      });
    } else { 
      // For budgets that exist but have no valid item, mark them
      (budgets || []).forEach((b) => {
        const bid = b.bid ?? b.id ?? b.budget_id;
        const found = (formData.invoice_items || []).find(it => String(it?.budget_id) === String(bid));
        const amountVal = found ? found.amount : undefined;
        if (amountVal === undefined || amountVal === "" || amountVal === null || isNaN(Number(amountVal))) {
          //fieldErrors[`budget_${bid}`] = "Enter an amount for this budget2.";
        }
      });
    }

    if (Object.keys(fieldErrors).length) {
      setValidationFields(fieldErrors);
      return null;
    }

    // Convert string values to appropriate types and filter valid items only
    const effectiveProjectId = project?.project_data?.id || project?.project_data?.project_id || formData.project_id;
    const effectiveClientId = project?.project_data?.client_id || formData.client_id;

    const base = {
      invoice_title: formData.invoice_title,
      invoice_date: formData.invoice_date,
      due_date: formData.due_date,
      billing_contact: formData.billing_contact,
      reference: formData.reference,
      description: formData.description,
      gst: String(parseFloat(formData.gst) || 0.15),
      client_id: parseInt(effectiveClientId),
      project_id: parseInt(effectiveProjectId),
      invoice_items: validItems.map(item => ({
        budget_id: parseInt(item.budget_id),
        amount: parseFloat(item.amount)
      }))
    };

    console.log("[Invoice/Create] Effective IDs -> client_id:", effectiveClientId, " project_id:", effectiveProjectId);
    console.log("[Invoice/Create] Raw formData IDs -> client_id:", formData.client_id, " project_id:", formData.project_id);
    return base;
  };

  const handleSubmitDraft = (e) => {
    e.preventDefault();
    const data = buildSubmitData();
    if (!data) return;
    const submitData = { ...data, status: 0 };
    console.log("Submitting invoice (draft):", submitData);
    wasSubmittingRef.current = true;
    dispatch({ type: CREATE_INVOICE_REQUEST, payload: submitData });
  };

  const handleSubmitForApproval = (e) => {
    e.preventDefault();
    const data = buildSubmitData();
    if (!data) return;
    const submitData = { ...data, status: 2 };
    console.log("Submitting invoice (for approval):", submitData);
    wasSubmittingRef.current = true;
    dispatch({ type: CREATE_INVOICE_REQUEST, payload: submitData });
  };

  const resetForm = () => {
    setFormData({
      client_id: clientId || project?.project_data?.client_id || "",
      project_id: projectId || project?.project_data?.pid || project?.project_data?.id || "",
      invoice_title: "",
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; })(),
      billing_contact: "",
      reference: "",
      description: "",
      gst: 0.15,
      invoice_items: [{ budget_id: "", amount: "" }],
    });
    // clear inline validation state when resetting form
    setValidationFields({});
    setDueDateTouched(false);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  // Helpers for UI
  // const [openBudgets, setOpenBudgets] = useState({});
  useEffect(() => {
    const initial = {};
    budgets.forEach((b) => { initial[b.bid] = true; });
    // setOpenBudgets(initial);
  }, [budgets]);

  // const toggleBudget = (bid) => setOpenBudgets((p) => ({ ...p, [bid]: !p[bid] }));

  // Compute subtotal using invoice item amounts when provided; otherwise fall back to the
  // budget's default amount (so totals reflect budget amounts before the user edits values).
  const subTotal = useMemo(() => {
    const items = Array.isArray(formData.invoice_items) ? formData.invoice_items : [];
    console.log("[Invoice] Items 1:", items);
    // if (Array.isArray(budgets) && budgets.length > 0) {
    //   // Only sum explicit invoice item amounts (user-entered). Do not fall back to budget record amounts.
    //   return budgets.reduce((sum, b) => {
    //     const bid = b.bid ?? b.id ?? b.budget_id;
    //     const matching = items.find(it => String(it?.budget_id) === String(bid));
    //     if (!matching) return sum;
    //     const v = matching.amount;
    //     const n = typeof v === 'string' && v === '' ? NaN : Number(v);
    //     return sum + (isNaN(n) ? 0 : n);
    //   }, 0);
    // }
    // Fallback: sum any numeric invoice item amounts (if no budgets available)
    //console.log("[Invoice] Items 2:", items);
    return items
      .filter((it) => it && it.amount !== "" && it.amount !== null && !isNaN(parseFloat(it.amount)) && Number(it.amount) !== 0)
      .reduce((s, it) => s + parseFloat(it.amount), 0);
  }, [formData.invoice_items, budgets]);
  
console.log("[Invoice] sub total:", subTotal);
  const gstAmount = useMemo(() => subTotal * (parseFloat(formData.gst) || 0), [subTotal, formData.gst]);
  const totalAmount = useMemo(() => subTotal + gstAmount, [subTotal, gstAmount]);

  // Updated layout and functionality to match the provided UI design
  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="new-invoice-dialog"
      contentClassName="new-invoice-modal"
    >
      <Modal.Header closeButton>
        <div className="w-100 d-flex justify-content-between align-items-center">
          <Modal.Title className="mb-0">
            New Invoice - {project?.project_data?.project_code} {project?.project_data?.project_name }
          </Modal.Title>
          </div>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Top section laid out exactly like the design */}
          <div className="invoice-top-grid">
            {/* Left block: Invoice To + Billing Contact on first row, Invoice Title below */}
            <div className="grid-left">
              <div className="grid-left-top">
                <Form.Group className="mb-2">
                  <Form.Label>Invoice To</Form.Label>
                  <Form.Control type="text" value={formData.client_name || ""} readOnly disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Billing Contact <span className="text-danger">*</span></Form.Label>
                  <Form.Control name="billing_contact" value={formData.billing_contact} onChange={handleInputChange} isInvalid={!!validationFields.billing_contact} readOnly disabled/>
                </Form.Group>
              </div>
              <Form.Group className="mb-0">
                <Form.Label>Invoice Title <span className="text-danger">*</span></Form.Label>
                <Form.Control name="invoice_title" value={formData.invoice_title} onChange={handleInputChange} isInvalid={!!validationFields.invoice_title} />
                <Form.Control.Feedback type="invalid">{validationFields.invoice_title}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                  <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleInputChange} isInvalid={!!validationFields.description} />
                  <Form.Control.Feedback type="invalid">{validationFields.description}</Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* Right block: two-by-two fields */}
            <div className="grid-right">
              <div className="top-right-grid">
                <Form.Group className="mb-2">
                  <Form.Label>Invoice Date</Form.Label>
                  <Form.Control type="date" name="invoice_date" value={formData.invoice_date} onChange={handleInputChange} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Due Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="date" name="due_date" value={formData.due_date} onChange={handleInputChange} isInvalid={!!validationFields.due_date} />
                  <Form.Control.Feedback type="invalid">{validationFields.due_date}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>GST(%)</Form.Label>
                  <Form.Control value={`${(parseFloat(formData.gst) || 0) * 100}`}/>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>Reference</Form.Label>
                    <Form.Control name="reference" value={formData.reference} onChange={handleInputChange} />
                </Form.Group>
              </div>
            </div>
          </div>

          {/* Budgets accordion */}
          <div className="budget-list-box">
          {budgets.map((b, idx) => {
            const bid = b.bid ?? b.id ?? b.budget_id;
           
            return (
                  <div key={b.bid} className="budget-block">
                  <div className="budget-header d-flex align-items-center justify-content-between p-2">
                  <div className="d-flex align-items-center gap-2">
                      {/* <button type="button" className="btn btn-link p-0 budget-toggle" onClick={() => toggleBudget(b.bid)} aria-label="toggle">
                        {openBudgets[b.bid] ? <BsChevronDown size={16} /> : <BsChevronRight size={16} />}
                      </button> */}
                    <span className="text-muted">BD{String(b.bid).padStart(3, "0")} - {b.budget_name}</span>
                  </div>
                  <div style={{ width: 100 }} className="d-flex align-items-center">
                    <div className="d-flex align-items-center w-100">
                      <span className="me-2 text-muted">$</span>
                      <Form.Control
                        className="text-end"
                        type="text"
                        style={{ flex: '1 1 auto' }}
                        isInvalid={!!validationFields[`budget_${bid}`]}
                        onChange={(e) => {
                          updateInvoiceItemAmountForBudget(bid, e.target.value);
                        }}
                        placeholder="0.00"
                        defaultValue={0}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationFields[`budget_${bid}`]}
                      </Form.Control.Feedback>
                    </div>
                  </div>
                </div>
                {/* {openBudgets[b.bid] && (
                  <div className="p-2">
                    <Table className="mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: "16%" }}>Date</th>
                          <th style={{ width: "18%" }}>Task</th>
                          <th style={{ width: "16%" }}>User</th>
                          <th>Comment</th>
                          <th style={{ width: "12%" }}>Worked Hours</th>
                          <th style={{ width: "10%" }}>Rate</th>
                          <th style={{ width: "12%" }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(b.tasks || []).length > 0 ? (
                          (b.tasks || []).map((t, i) => (
                            <tr key={i}>
                              <td>{t.task_start_date || ""}{t.task_end_date ? ` - ${t.task_end_date}` : ""}</td>
                              <td>{t.task_name || "-"}</td>
                              <td>{(t.assignments && t.assignments[0]) ? `${t.assignments[0].first_name} ${t.assignments[0].last_name}` : "-"}</td>
                              <td>{t.comment || ""}</td>
                              <td>{Number(t.hours_spent || 0).toFixed(2)} hr</td>
                              <td>{t.rate ? `$${Number(t.rate).toFixed(2)}` : "-"}</td>
                              <td className="text-end">${budgetAmountDisplay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="text-center text-muted">No items</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )} */}
              </div>
            );
          })}
          </div>

          {/* Single divider line at end of budget list */}
          <div className="budget-list-end" />

          {/* Totals summary */}
          <div className="mt-3 d-flex justify-content-end totals-summary">
            <div className="totals-box">
              <div className="d-flex justify-content-between totals-line"><span className="totals-label">Sub Total</span><span className="totals-value">${subTotal.toFixed(2)}</span></div>
              <div className="d-flex justify-content-between totals-line"><span className="totals-label">GST {(parseFloat(formData.gst) || 0) * 100}%</span><span className="totals-value">${gstAmount.toFixed(2)}</span></div>
              <hr />
              <div className="d-flex justify-content-between fw-bold"><span>TOTAL (NZD)</span><span>${totalAmount.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="d-flex justify-content-start gap-2 mt-4">
            <Button variant="primary" type="button" disabled={createLoading} onClick={handleSubmitDraft}>
              {createLoading ? "Saving..." : "Save as Draft"}
            </Button>
            <Button variant="primary" type="button" disabled={createLoading} onClick={handleSubmitForApproval}>
              Submit to Xero
            </Button>
            <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default NewInvoiceModal;